import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { resolveImageUrl, NEWS_PLACEHOLDER } from '../utils/imageHelper';

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { error } = useToast();

  useEffect(() => {
    if (!postId) {
      navigate('/news');
      return;
    }

    const fetchPost = async () => {
      try {
        // ✅ Correct route — your backend uses /posts/:id not /news/posts/:id
        const { data } = await api.get(`/posts/${postId}`);
        if (!data) {
          error('Post not found.');
          navigate('/news');
          return;
        }
        setPost(data);
      } catch (err) {
        console.error('Failed to fetch post:', err);
        if (err.status === 404 || err.response?.status === 404) {
          error('Post not found.');
        } else {
          error('Failed to load post. Please try again.');
        }
        navigate('/news');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, error, navigate]);

  // Collect all images from all possible fields
  const getPostImages = (post) => {
    const images = [];
    if (post.images && Array.isArray(post.images)) {
      post.images.forEach(img => {
        const url = resolveImageUrl(img, null);
        if (url) images.push(url);
      });
    }
    if (!images.length) {
      const single = resolveImageUrl(post.imageUrl || post.image, null);
      if (single) images.push(single);
    }
    return images;
  };

  const formatDate = (post) => {
    try {
      if (post.createdAt?.seconds) {
        return new Date(post.createdAt.seconds * 1000).toLocaleDateString('en-IN', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        });
      }
      if (post.createdAt?.toDate) {
        return post.createdAt.toDate().toLocaleDateString('en-IN', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        });
      }
      if (typeof post.createdAt === 'string') {
        return new Date(post.createdAt).toLocaleDateString('en-IN', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        });
      }
    } catch (e) {}
    return '';
  };

  if (loading) {
    return (
      <div className="post-loading">
        <div className="post-loading__pot">📰</div>
        <p>Loading article...</p>
      </div>
    );
  }

  if (!post) return null;

  const images = getPostImages(post);
  const heroImage = images[0] || NEWS_PLACEHOLDER;
  const galleryImages = images.slice(1);
  const dateStr = formatDate(post);

  return (
    <div className="post-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">›</span>
          <Link to="/news">News</Link>
          <span aria-hidden="true">›</span>
          <span>{post.title}</span>
        </nav>

        <article className="post-article">
          {/* Header */}
          <header className="post-article__header">
            <div className="post-meta">
              {post.category && (
                <span className="cat-pill">{post.category}</span>
              )}
              {dateStr && (
                <span className="post-date">📅 {dateStr}</span>
              )}
              {post.author && (
                <span className="post-author">✍️ {post.author}</span>
              )}
            </div>
            <h1>{post.title}</h1>
          </header>

          {/* Hero Image */}
          <div className="post-article__hero">
            <img
              src={heroImage}
              alt={post.title}
              onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
            />
          </div>

          {/* Content */}
          <div className="post-article__body">
            {post.content
              ? post.content
                  .split('\n')
                  .map((para, i) => para.trim() ? <p key={i}>{para}</p> : null)
                  .filter(Boolean)
              : <p>No content available for this post.</p>
            }
          </div>

          {/* Gallery */}
          {galleryImages.length > 0 && (
            <div className="post-article__gallery">
              <h3>📷 Photo Gallery</h3>
              <div className="gallery-grid">
                {galleryImages.map((img, i) => (
                  <div key={i} className="gallery-item">
                    <img
                      src={img}
                      alt={`${post.title} ${i + 2}`}
                      onError={(e) => { e.target.src = NEWS_PLACEHOLDER; }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="post-article__footer">
            <Link to="/news" className="btn btn-outline">
              ← Back to News
            </Link>
          </div>
        </article>
      </div>

      <style jsx>{`
        .post-detail-page {
          min-height: 100vh;
          background: var(--neutral-50);
          padding: var(--space-8) 0 var(--space-16);
        }

        .post-loading {
          min-height: 60vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--space-4);
          color: var(--neutral-600);
        }
        .post-loading__pot {
          font-size: 3rem;
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.95); }
        }

        /* Breadcrumb */
        .breadcrumb {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          margin-bottom: var(--space-6);
          font-size: var(--text-sm);
          flex-wrap: wrap;
          max-width: 820px;
          margin-left: auto;
          margin-right: auto;
        }
        .breadcrumb a {
          color: var(--primary-500);
          text-decoration: none;
          font-weight: var(--weight-medium);
          transition: color var(--transition-fast);
        }
        .breadcrumb a:hover { color: var(--primary-700); }
        .breadcrumb span {
          color: var(--neutral-400);
        }
        .breadcrumb span:last-child {
          color: var(--neutral-600);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 300px;
        }

        /* Article */
        .post-article {
          max-width: 820px;
          margin: 0 auto;
          background: white;
          border-radius: var(--radius-2xl);
          padding: var(--space-10);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--neutral-200);
        }

        /* Header */
        .post-article__header {
          margin-bottom: var(--space-8);
          padding-bottom: var(--space-6);
          border-bottom: 2px solid var(--neutral-200);
        }
        .post-article__header h1 {
          font-size: var(--text-4xl);
          line-height: var(--leading-tight);
          color: var(--primary-900);
          margin-top: var(--space-4);
        }

        /* Post Meta */
        .post-meta {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          flex-wrap: wrap;
          margin-bottom: var(--space-3);
        }
        .cat-pill {
          font-size: var(--text-xs);
          font-weight: var(--weight-bold);
          color: var(--primary-600);
          background: var(--primary-50);
          padding: var(--space-1) var(--space-3);
          border-radius: var(--radius-full);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid var(--primary-200);
        }
        .post-date, .post-author {
          font-size: var(--text-sm);
          color: var(--neutral-500);
        }

        /* Hero Image */
        .post-article__hero {
          margin-bottom: var(--space-8);
          border-radius: var(--radius-xl);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          background: var(--neutral-100);
        }
        .post-article__hero img {
          width: 100%;
          height: auto;
          max-height: 480px;
          object-fit: cover;
          display: block;
        }

        /* Body */
        .post-article__body {
          font-size: var(--text-lg);
          line-height: var(--leading-relaxed);
          color: var(--neutral-800);
          margin-bottom: var(--space-8);
        }
        .post-article__body p {
          margin-bottom: var(--space-5);
          color: var(--neutral-800);
        }

        /* Gallery */
        .post-article__gallery {
          margin-bottom: var(--space-8);
          padding-top: var(--space-6);
          border-top: 2px solid var(--neutral-200);
        }
        .post-article__gallery h3 {
          font-size: var(--text-xl);
          color: var(--primary-800);
          margin-bottom: var(--space-4);
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: var(--space-4);
        }
        .gallery-item {
          border-radius: var(--radius-lg);
          overflow: hidden;
          aspect-ratio: 4/3;
          box-shadow: var(--shadow-sm);
          background: var(--neutral-100);
        }
        .gallery-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-base);
        }
        .gallery-item:hover img {
          transform: scale(1.05);
        }

        /* Footer */
        .post-article__footer {
          padding-top: var(--space-6);
          border-top: 1px solid var(--neutral-200);
        }

        @media (max-width: 768px) {
          .post-article {
            padding: var(--space-5);
          }
          .post-article__header h1 {
            font-size: var(--text-2xl);
          }
          .post-article__body {
            font-size: var(--text-base);
          }
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default PostDetailPage;